"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useRef, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import type { ProgressWeightTrend, WeightEntry } from "@bitebalance/contracts"
import { useI18n } from "@/i18n"

/**
 * Weigh-in chart — 2026-08-29 owner redesign ("floating dots with no line
 * is really fucked up"): the journey is now READABLE and alive:
 *
 *  - weigh-ins connect into a smooth green line with a lime gradient area
 *    beneath — the shape of the journey at a glance, not a dot puzzle;
 *  - the ADR-027 7-day rolling trend draws in CORAL on top (only when the
 *    server sends it — never fabricated);
 *  - hover/touch scrubbing: a crosshair + tooltip shows each weigh-in's
 *    date, weight, and the change vs the previous one;
 *  - round-number gridlines instead of raw min/max;
 *  - a delta badge in the header ("▼ 2,1 kg since 21 Jun") — the change is
 *    shown factually, never judged (claims policy: direction words stay
 *    neutral, goals differ);
 *  - animated draw-in + a soft pulse on the latest point, reduce-motion
 *    aware. Numbers rendered are exactly the stored entries — no smoothing
 *    of VALUES, only of the connecting curve.
 */

interface WeightChartProps {
  entries: WeightEntry[]
  trend: ProgressWeightTrend
}

const W = 640
const H = 220
const PAD = { top: 18, right: 56, bottom: 28, left: 12 }

interface Pt {
  x: number
  y: number
}

/** Catmull-Rom → cubic bezier: smooth curve THROUGH every real point. */
function smoothPath(pts: Pt[]): string {
  if (pts.length === 0) return ""
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[Math.min(pts.length - 1, i + 2)]
    const c1x = p1.x + (p2.x - p0.x) / 6
    const c1y = p1.y + (p2.y - p0.y) / 6
    const c2x = p2.x - (p3.x - p1.x) / 6
    const c2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`
  }
  return d
}

/** Round-number axis ticks (1/2/5 steps) — "80 kg", never "79,4 kg". */
function niceTicks(lo: number, hi: number, target = 4): number[] {
  const span = Math.max(hi - lo, 0.5)
  const rawStep = span / Math.max(target - 1, 1)
  const mag = 10 ** Math.floor(Math.log10(rawStep))
  const norm = rawStep / mag
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag
  const ticks: number[] = []
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(v)
  return ticks
}

export function WeightChart({ entries, trend }: WeightChartProps) {
  const { t, locale } = useI18n()
  const reduce = useReducedMotion()
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hover, setHover] = useState<number | null>(null)
  if (entries.length === 0) return null

  const sorted = [...entries].sort((a, b) => a.measuredAt.localeCompare(b.measuredAt))
  const trendSeries = trend?.series ?? []

  const allDates = [...sorted.map(e => e.measuredAt), ...trendSeries.map(p => p.date)].sort()
  const minDate = new Date(`${allDates[0]}T00:00:00Z`).getTime()
  const maxDate = new Date(`${allDates[allDates.length - 1]}T00:00:00Z`).getTime()
  const dateSpan = Math.max(maxDate - minDate, 1)

  const allKg = [...sorted.map(e => e.weightKg), ...trendSeries.map(p => p.sevenDayAvgKg)]
  const minKg = Math.min(...allKg)
  const maxKg = Math.max(...allKg)
  const kgPad = Math.max((maxKg - minKg) * 0.15, 0.6)
  const lowKg = minKg - kgPad
  const highKg = maxKg + kgPad

  const x = (date: string) =>
    PAD.left +
    ((new Date(`${date}T00:00:00Z`).getTime() - minDate) / dateSpan) * (W - PAD.left - PAD.right)
  const y = (kg: number) =>
    PAD.top + (1 - (kg - lowKg) / (highKg - lowKg)) * (H - PAD.top - PAD.bottom)

  const pts: Pt[] = sorted.map(e => ({ x: x(e.measuredAt), y: y(e.weightKg) }))
  const linePath = smoothPath(pts)
  const areaPath =
    pts.length >= 2
      ? `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${H - PAD.bottom} L ${pts[0].x.toFixed(1)} ${H - PAD.bottom} Z`
      : ""
  const trendPath = smoothPath(trendSeries.map(p => ({ x: x(p.date), y: y(p.sevenDayAvgKg) })))
  const ticks = niceTicks(lowKg, highKg)

  const fmtDate = new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-DE", {
    day: "numeric",
    month: "short",
  })
  const nf = new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-DE", {
    maximumFractionDigits: 1,
  })
  const fmtKg = (kg: number) => `${nf.format(kg)} kg`

  const first = sorted[0]
  const latest = sorted[sorted.length - 1]
  const totalDelta = latest.weightKg - first.weightKg
  const deltaLabel = `${totalDelta > 0 ? "▲" : totalDelta < 0 ? "▼" : "→"} ${fmtKg(Math.abs(totalDelta))}`

  function onPointerMove(event: React.PointerEvent<SVGSVGElement>) {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const viewX = ((event.clientX - rect.left) / rect.width) * W
    let nearest = 0
    let best = Number.POSITIVE_INFINITY
    pts.forEach((p, i) => {
      const d = Math.abs(p.x - viewX)
      if (d < best) {
        best = d
        nearest = i
      }
    })
    setHover(nearest)
  }

  const hovered = hover !== null ? sorted[hover] : null
  const hoveredPrev = hover !== null && hover > 0 ? sorted[hover - 1] : null
  const hoverDelta = hovered && hoveredPrev ? hovered.weightKg - hoveredPrev.weightKg : null

  return (
    <div className="flex flex-col gap-2 rounded-bb-xl border border-line bg-paper p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-ink">{t("insights.progress.chartTitle")}</span>
        <div className="flex items-center gap-2">
          {sorted.length >= 2 && (
            <motion.span
              initial={reduce ? false : { scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.4 }}
              className="rounded-bb-full bg-lime/60 px-2.5 py-0.5 text-xs font-bold text-ink"
              title={t("insights.progress.chartSinceStart", {
                date: fmtDate.format(new Date(`${first.measuredAt}T00:00:00Z`)),
              })}
            >
              {deltaLabel}{" "}
              <span className="font-medium text-ink-2">
                {t("insights.progress.chartSinceStart", {
                  date: fmtDate.format(new Date(`${first.measuredAt}T00:00:00Z`)),
                })}
              </span>
            </motion.span>
          )}
          <span className="text-sm text-ink-2">
            <strong className="text-ink">{fmtKg(latest.weightKg)}</strong>
          </span>
        </div>
      </div>

      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={t("insights.progress.chartTitle")}
          className="w-full touch-pan-y"
          onPointerMove={onPointerMove}
          onPointerLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id="bb-weight-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--bb-lime)" stopOpacity="0.55" />
              <stop offset="100%" stopColor="var(--bb-lime)" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* round-number gridlines */}
          {ticks.map(kg => (
            <g key={kg}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(kg)}
                y2={y(kg)}
                className="stroke-line"
                strokeDasharray="3 5"
              />
              <text x={W - PAD.right + 6} y={y(kg) + 4} className="fill-muted-foreground text-[11px]">
                {fmtKg(kg)}
              </text>
            </g>
          ))}

          {/* gradient area under the weigh-in line */}
          {areaPath && (
            <motion.path
              d={areaPath}
              fill="url(#bb-weight-area)"
              initial={reduce ? undefined : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            />
          )}

          {/* the connected weigh-in journey */}
          {pts.length >= 2 && (
            <motion.path
              d={linePath}
              fill="none"
              stroke="var(--bb-green-2)"
              strokeWidth={2.5}
              strokeLinecap="round"
              initial={reduce ? undefined : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          )}

          {/* ADR-027 rolling 7-day trend — coral, only when the server sends it */}
          {trendPath && trendSeries.length >= 2 && (
            <motion.path
              d={trendPath}
              fill="none"
              stroke="var(--bb-coral)"
              strokeWidth={3}
              strokeLinecap="round"
              initial={reduce ? undefined : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.5 }}
            />
          )}

          {/* crosshair for the hovered weigh-in */}
          {hovered && hover !== null && (
            <line
              x1={pts[hover].x}
              x2={pts[hover].x}
              y1={PAD.top}
              y2={H - PAD.bottom}
              stroke="var(--bb-green-2)"
              strokeOpacity={0.35}
              strokeDasharray="2 3"
            />
          )}

          {/* raw weigh-ins — every logged entry is a visible, real dot */}
          {sorted.map((entry, i) => {
            const isLatest = i === sorted.length - 1
            return (
              <motion.circle
                key={entry.measuredAt + i}
                cx={pts[i].x}
                cy={pts[i].y}
                r={hover === i ? 6.5 : isLatest ? 5.5 : 4}
                fill={isLatest ? "var(--bb-green)" : "var(--bb-green-2)"}
                stroke="var(--bb-paper)"
                strokeWidth={2}
                initial={reduce ? undefined : { scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: reduce ? 0 : 0.2 + i * 0.06, type: "spring", stiffness: 300 }}
              />
            )
          })}

          {/* soft pulse on the latest point */}
          {!reduce && (
            <motion.circle
              cx={pts[pts.length - 1].x}
              cy={pts[pts.length - 1].y}
              r={6}
              fill="none"
              stroke="var(--bb-green)"
              strokeWidth={1.5}
              initial={{ scale: 1, opacity: 0.7 }}
              animate={{ scale: 2.2, opacity: 0 }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut", delay: 1 }}
              style={{ transformOrigin: `${pts[pts.length - 1].x}px ${pts[pts.length - 1].y}px` }}
            />
          )}

          {/* first/last date labels */}
          <text x={PAD.left} y={H - 6} className="fill-muted-foreground text-[11px]">
            {fmtDate.format(new Date(`${first.measuredAt}T00:00:00Z`))}
          </text>
          <text x={W - PAD.right} y={H - 6} textAnchor="end" className="fill-muted-foreground text-[11px]">
            {fmtDate.format(new Date(`${latest.measuredAt}T00:00:00Z`))}
          </text>
        </svg>

        {/* scrub tooltip — real stored values only */}
        {hovered && hover !== null && (
          <div
            role="status"
            className="pointer-events-none absolute -translate-x-1/2 rounded-bb-md border border-line bg-paper px-2.5 py-1.5 text-xs shadow-md"
            style={{
              left: `${(pts[hover].x / W) * 100}%`,
              top: `${Math.max((pts[hover].y / H) * 100 - 22, 0)}%`,
            }}
          >
            <span className="block font-bold text-ink">{fmtKg(hovered.weightKg)}</span>
            <span className="block text-muted-foreground">
              {fmtDate.format(new Date(`${hovered.measuredAt}T00:00:00Z`))}
              {hoverDelta !== null && (
                <>
                  {" · "}
                  <span className={hoverDelta === 0 ? "" : "font-semibold text-ink-2"}>
                    {hoverDelta > 0 ? "+" : ""}
                    {nf.format(hoverDelta)} kg
                  </span>
                </>
              )}
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block size-2 rounded-full bg-green-2" />
          {t("insights.progress.chartEntriesLegend")}
        </span>
        {trendPath && trendSeries.length >= 2 && (
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-0.5 w-4 rounded bg-coral" />
            {t("insights.progress.chartTrendLegend")}
          </span>
        )}
      </div>
    </div>
  )
}
